@let data = (data$ | async);
@let templates = data ? data.templates : [];
@if (data) {
  <div class="dca-heading flex items-center justify-between px2 col-12">
    <div class="h2 flex items-center col-12">
      @if (isDcaMaximized()) {
        <button
          mat-icon-button
          aria-label="Close the Configuration"
          matTooltip="Close Template Configuration"
          (click)="closeMetaDataEditor()"
        >
          <mat-icon aria-label="Open/Hide DCA">close</mat-icon>
        </button>
      }
      <span>Template Configuration</span>
      @if (isDcaMaximized()) {
        <div class="flex flex-auto"></div>
        <div class="clearfix mxn2 pr2">
          <div class="col px1">
            <mat-button-toggle-group
              name="uiVisibilityRoles"
              aria-label="Visibility Roles"
              [value]="selectedUiRoles"
              (change)="onToggleChange($event.value)"
              multiple
              matTooltip="Change displayed categories"
            >
              @for (ui of uiVisibilityRoles; track ui.id) {
                <mat-button-toggle [value]="ui.id">
                  {{ ui.label }}
                </mat-button-toggle>
              }
            </mat-button-toggle-group>
          </div>
          <div class="col px1">
            <button
              mat-icon-button
              color="outline"
              (click)="downloadTemplate()"
              matTooltip="Copy the YAML configuration to create a new template with the current configuration"
            >
              <mat-icon>copy_all</mat-icon>
            </button>
          </div>
        </div>
      }
    </div>
  </div>
} @else {
  <div class="dca-heading flex items-center justify-between px2">
    <div class="h2 flex items-center">
      <span>Loading ...</span>
    </div>
  </div>
}

@if (loading$ | async) {
  <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}

<div class="dca-scrollarea">
  <div class="flex">
    <div class="flex">
      @if (isDcaShown() && !isDcaExpanded()) {
        <div class="dca-width">
          @if (data) {
            <mat-form-field appearance="fill" class="col-12 m0 p0">
              <mat-label>Template</mat-label>
              <mat-select
                name="template"
                (selectionChange)="navigateToTemplate($event.value)"
                [value]="data.template?.slug"
                [compareWith]="compareTemplate"
              >
                @for (template of templates; track template.slug) {
                  <mat-option [value]="template.slug">
                    {{ template.title || template.slug }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>

            @if (activeTemplate$ | async; as template) {
              <div class="m1 p1 mt0 pt0">
                @if (template.description) {
                  <p>
                    <i>{{ template.description }}</i>
                  </p>
                }
                <div class="mb1">
                  <p>
                    The export options will use the manuscript and any author data that was provided
                    to export into various formats.
                  </p>
                </div>

                <div class="mt2 col-12">
                  @if (data.template?.locales?.length > 1) {
                    <mat-form-field class="col-12" appearance="outline">
                      <mat-label>Template languages</mat-label>
                      <mat-select
                        name="locale"
                        #locale
                        [value]="getInitialLocale(data.template?.locales, data.editor.id)"
                        (selectionChange)="onLocaleChange($event.value, data.editor.id)"
                      >
                        @for (localeOpt of data.template.locales; track localeOpt.value) {
                          <mat-option [value]="localeOpt.value">{{ localeOpt.label }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                  @if (data.template?.citationStyles?.length > 1) {
                    <mat-form-field class="col-12" appearance="outline">
                      <mat-label>Citation style for export</mat-label>
                      <mat-select
                        name="citationStyle"
                        #citationStyle
                        [value]="data.template?.citationStyles[0].id"
                      >
                        @for (style of data.template?.citationStyles; track style) {
                          <mat-option [value]="style.id">{{ style.id }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                </div>

                @let hasUncited = (hasUncitedReferences$ | async);
                <div class="flex col-12 mx-auto py1 justify-between">
                  <div class="flex">
                    <button
                      mat-stroked-button
                      [disabled]="!canEdit(data.editor.key)"
                      color="primary"
                      class="mt1"
                      (click)="exportWithTemplate(data.template?.slug, data.editor.key, 'princexml')"
                      matTooltip="PDF (PrinceXML publications with high layout requirements)"
                    >
                      Download PDF
                    </button>
                    <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="more formats">
                      <mat-icon>expand_more</mat-icon>
                    </button>
                  </div>
                  <span>
                    @if (data?.template?.metaData) {
                      <button
                        mat-icon-button
                        matTooltip="Open Template Configuration"
                        (click)="toggleMetaData()"
                      >
                        <mat-icon>settings</mat-icon>
                      </button>
                    }
                    <button
                      mat-icon-button
                      (click)="resetMetaData()"
                      matTooltip="Reset settings to template defaults"
                    >
                      <mat-icon>restart_alt</mat-icon>
                    </button>

                    <mat-menu #menu="matMenu">
                      @if (hasLabel(data.template, 'reader')) {
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          matTooltip="Preview the document in reader"
                          [disabled]="!canEdit(data.editor.key)"
                          (click)="openReader(data.template?.slug, data.editor.key)"
                        >
                          <b>Reader</b>
                        </button>
                        <button
                          mat-menu-item
                          [disabled]="!canEdit(data.editor.key)"
                          matTooltip="Export data snapshot for your own reader"
                          matTooltipPosition="before"
                          (click)="exportReaderSnapshot(data.template?.slug, data.editor.key)"
                        >
                          <b>Reader Snapshot</b> (ZIP)
                        </button>
                      }

                      <mat-divider></mat-divider>

                      @if (hasLabel(data.template, 'lens')) {
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          [disabled]="!canEdit(data.editor.key)"
                          (click)="openLens(data.template?.slug, data.editor.key, 'xml')"
                        >
                          <b>Lens Viewer</b> (Deprecated viewer for JATS XML)
                        </button>
                      }
                      @if (hasLabel(data.template, 'jatsxml')) {
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          [disabled]="!canEdit(data.editor.key)"
                          (click)="exportWithTemplate(data.template?.slug, data.editor.key, 'jatsxml')"
                        >
                          <b>JATS XML</b>
                        </button>
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          matTooltip="Download JATS XML with assets as ZIP"
                          [disabled]="!canEdit(data.editor.key)"
                          (click)="exportJatsXmlZip(data.template?.slug, data.editor.key)"
                        >
                          <b>JATS XML</b> (ZIP)
                        </button>
                      }
                      @if (hasLabel(data.template, 'bitsxml')) {
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          [disabled]="!canEdit(data.editor.key)"
                          (click)="exportWithTemplate(data.template?.slug, data.editor.key, 'bitsxml')"
                        >
                          <b>BITS XML</b>
                        </button>
                      }

                      <mat-divider></mat-divider>
                      @if (data.template?.runners?.includes('pagedjs')) {
                        <button
                          mat-menu-item
                          [disabled]="!canEdit(data.editor.key)"
                          matTooltipPosition="before"
                          matTooltip="Paged.js - Print PDF in browser (for publications with less strict layout requirements)"
                          (click)="exportWithTemplate(data.template?.slug, data.editor.key, 'pagedjs')"
                        >
                          <b>PDF alternative</b> (Paged.js)
                        </button>
                      }

                      <mat-divider></mat-divider>
                      @if (data.template?.runners?.includes('html')) {
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          [disabled]="!canEdit(data.editor.key)"
                          (click)="exportWithTemplate(data.template?.slug, data.editor.key, 'html')"
                        >
                          <b>HTML</b>
                        </button>
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          matTooltip="Download HTML with assets as ZIP"
                          [disabled]="!canEdit(data.editor.key)"
                          (click)="exportHtmlZip(data.template?.slug, data.editor.key)"
                        >
                          <b>HTML</b> (ZIP)
                        </button>
                      }
                      @if (data.template?.runners?.includes('epub')) {
                        <button
                          mat-menu-item
                          matTooltipPosition="before"
                          [disabled]="!canEdit(data.editor.key)"
                          matTooltip="Download EPUB"
                          (click)="exportWithTemplate(data.template?.slug, data.editor.key, 'epub')"
                        >
                          <b>EPUB</b>
                        </button>
                      }

                      <mat-divider></mat-divider>
                      <button
                        mat-menu-item
                        [disabled]="!canEdit(data.editor.key)"
                        matTooltip="Export Data Snapshot"
                        matTooltipPosition="before"
                        (click)="exportSnapshot(data.template?.slug, data.editor.key)"
                      >
                        <b>Data Snapshot</b> (ZIP)
                      </button>
                      <!--
                        One row per OJS release the template offers. This is still ONE export —
                        same label, same route, same archive — listed once per target release
                        rather than hidden behind a separate control the user has to find first.
                        The releases are not interchangeable (their schemas reject each other's
                        shape), so which one you are producing belongs on the thing you click.
                      -->
                      @if (hasLabel(data.template, 'ojsxml')) {
                        @for (version of data.template?.ojsVersions; track version.id) {
                          <button
                            mat-menu-item
                            [disabled]="!canEdit(data.editor.key)"
                            [matTooltip]="'Export OJS native XML for ' + version.label"
                            matTooltipPosition="before"
                            (click)="exportOJSSnapshot(data.template?.slug, data.editor.key, version.id)"
                          >
                            <b>{{ version.label }}</b> XML (ZIP)
                          </button>
                        }
                      }
                    </mat-menu>
                  </span>
                </div>
                @let isDirty = (isDocumentStateDirty$ | async);
                @if (isDirty || hasUncited) {
                  <div class="export-notices">
                    @if (isDirty) {
                      <div class="export-notice export-notice--warning">
                        <mat-icon>edit_note</mat-icon>
                        <span
                          >Document has unsaved changes — the export may not reflect the latest
                          edits.</span
                        >
                      </div>
                    }
                    @if (hasUncited) {
                      @if (uncitedReferences$ | async; as uncitedReferences) {
                        <div class="export-notice export-notice--info">
                          <mat-icon>format_quote</mat-icon>
                          <div class="export-notice__content">
                            <span
                              >{{ uncitedReferences.length }} uncited reference{{ uncitedReferences.length === 1 ? '' : 's' }}
                              will not appear in the bibliography.</span
                            >
                            <details class="export-notice__details">
                              <summary>Show references</summary>
                              <ul>
                                @for (reference of uncitedReferences; track reference.key) {
                                  <li>
                                    @if (reference.csl?.rawCitation) {
                                      <div [innerHTML]="reference.csl?.rawCitation"></div>
                                    } @else if (reference.bib) {
                                      {{ reference.bib }}
                                    } @else {
                                      {{ reference.csl?.id || reference.key }}
                                    }
                                  </li>
                                }
                              </ul>
                            </details>
                          </div>
                        </div>
                      }
                    }
                  </div>
                }
              </div>
            } @else {
              <p class="m2">
                <b>Please select a template above.</b>
              </p>
            }
          }
        </div>
      }
    </div>

    @if (isDcaShown() && isDcaExpanded()) {
      <div class="col-12">
        <sfo-export-settings [selectedUi]="selectedUiRoles"></sfo-export-settings>
      </div>
    }
  </div>
</div>
